import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import { useAuth } from '../../context/AuthContext';
import './CreditSale.css';

const STATUSES = ['All', 'Pending', 'Partial', 'Paid'];

// Multi-row create form: each row is an independent credit sale (its own
// customer + product + qty + rate), mirroring the Daily Receipts form.
const makeEmptyRow = () => ({ customer_id: '', product_type: '', qty: '', rate: '', amount: '', remarks: '' });
const makeEmptyForm = (d) => ({ sale_date: d || today(), items: [makeEmptyRow()] });

export default function CreditSale({ entryDate }) {
  const { t: tr } = useTranslation();
  const { user, can, canAccess } = useAuth();
  const isAdmin = can('credit_sales', 'approve');
  const [view, setView] = useState('Parties'); // Toggle between 'Parties' and 'Transactions'
  // Inside Daily Entry the transactions list is retrieved for the business date.
  const [filterDate, setFilterDate] = useState(entryDate || '');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterCustId, setFilterCustId] = useState(''); // Store actual customer_id for drill-down
  const [searchQuery, setSearchQuery] = useState(''); // For party search box

  // Main data fetch
  const { data, loading, error, reload } = useApi(() => api.creditSales.summary({
    date: filterDate,
    status: filterStatus === 'All' ? '' : filterStatus,
    customer_id: filterCustId
  }), [filterDate, filterStatus, filterCustId]);

  const { data: products } = useApi(() => api.products.list(), []);

  const transactions = data?.transactions || [];
  const customers = data?.customers || [];

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(makeEmptyForm(entryDate));

  // Seed the first row's product once the product list loads (create form
  // only, and only while the form is still pristine).
  useEffect(() => {
    if (products?.length > 0) {
      setForm(f => {
        if (f.items.length !== 1 || f.items[0].product_type) return f;
        return { ...f, items: [{ ...f.items[0], product_type: products[0].code }] };
      });
    }
  }, [products]);

  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPayment, setShowPayment] = useState(null);
  const [payAmt, setPayAmt] = useState('');
  const [msg, setMsg] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  if (!canAccess('credit_sales')) {
    return (
      <div className="credit-sale-page">
        <ErrBanner msg={tr('creditSale.accessDenied')} />
      </div>
    );
  }

  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const resetForm = () => setForm(makeEmptyForm(entryDate));

  // ── Multi-row create handlers ─────────────────────────────────────────
  // New rows inherit the previous row's customer / product / rate so a run
  // of sales to the same party stays quick to enter.
  const addItem = () => {
    setForm(prev => {
      const last = prev.items[prev.items.length - 1];
      const row = makeEmptyRow();
      if (last) {
        row.customer_id  = last.customer_id  || '';
        row.product_type = last.product_type || '';
        row.rate         = last.rate         || '';
      }
      return { ...prev, items: [...prev.items, row] };
    });
  };

  const removeItem = (idx) => setForm(prev => ({
    ...prev,
    items: prev.items.length === 1 ? prev.items : prev.items.filter((_, i) => i !== idx),
  }));

  const updateItem = (idx, field, value) => {
    setForm(prev => {
      const items = prev.items.map((it, i) => {
        if (i !== idx) return it;
        const next = { ...it, [field]: value };
        // Prefill rate from the product master when a product is picked and
        // the row's rate is still blank (never clobber a manual override).
        if (field === 'product_type' && !next.rate) {
          const p = (products || []).find(x => x.code === value);
          if (p?.rate != null) next.rate = String(p.rate);
        }
        // Keep amount = qty × rate in sync when qty/rate/product change.
        if (field === 'qty' || field === 'rate' || field === 'product_type') {
          const q = parseFloat(field === 'qty' ? value : next.qty) || 0;
          const r = parseFloat(field === 'rate' ? value : next.rate) || 0;
          if (q && r) next.amount = (q * r).toFixed(2);
        }
        return next;
      });
      return { ...prev, items };
    });
  };

  // Save each valid row as its own credit sale. credit_sales.php has no
  // batch endpoint, so we loop one create() per row. Rows that fail stay on
  // the form (with a reason) so only those can be retried.
  const handleSaveNew = async () => {
    const valid = form.items.filter(it => it.customer_id && Number(it.amount) > 0);
    if (valid.length === 0) { notify(tr('creditSale.toasts.addAtLeastOne')); return; }

    setSaving(true);
    const failed = [];
    let saved = 0;
    for (const it of valid) {
      try {
        await api.creditSales.create({
          sale_date:    form.sale_date,
          customer_id:  it.customer_id,
          product_type: it.product_type,
          qty:          it.qty,
          rate:         it.rate,
          amount:       it.amount,
          remarks:      it.remarks,
        });
        saved++;
      } catch (e) {
        failed.push({ row: it, message: e.message });
      }
    }
    setSaving(false);

    if (failed.length === 0) {
      notify(tr('creditSale.toasts.saved', { count: saved }));
      setShowForm(false); resetForm(); reload();
      return;
    }
    // Keep only the unsaved rows on the form for retry.
    setForm(prev => ({ ...prev, items: failed.map(f => f.row) }));
    notify(tr('creditSale.toasts.savedFailed', { saved, failed: failed.length, messages: failed.map(f => f.message).join('; ') }));
    reload();
  };

  const handleSaveEdit = async () => {
    if (!editRow.customer_id || !editRow.amount) { notify(tr('creditSale.toasts.customerAmountRequired')); return; }
    setSaving(true);
    try {
      await api.creditSales.update(editRow.id, {
        amount:       editRow.amount,
        qty:          editRow.qty,
        rate:         editRow.rate,
        product_type: editRow.product_type,
        remarks:      editRow.remarks,
      });
      notify(tr('creditSale.toasts.creditEntryUpdated'));
      setEditRow(null);
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.creditSales.remove(confirmDelete.id);
      notify(tr('creditSale.toasts.entryDeleted'));
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setConfirmDelete(null);
  };

  const collect = async (id) => {
    const amt = parseFloat(payAmt);
    if (!amt || amt <= 0) { notify(tr('creditSale.toasts.enterValidAmount')); return; }
    try {
      await api.creditSales.collect(id, amt);
      notify(tr('creditSale.toasts.paymentCollected'));
      setShowPayment(null); setPayAmt('');
      reload();
    } catch (e) { notify('❌ ' + e.message); }
  };

  const totalOutstanding = (customers || []).reduce((s, c) => s + Number(c.balance || 0), 0);
  const grandTotal = form.items.reduce((s, it) => s + (Number(it.amount) || 0), 0);

  const openEdit = (row) => {
    setShowForm(false);
    setEditRow({
      id: row.id,
      sale_date: row.sale_date,
      customer_id: row.customer_id,
      customer_name: row.party_name,
      qty: row.qty || '',
      rate: row.rate || '',
      amount: row.amount,
      product_type: row.product_code || '',
      remarks: row.remarks || ''
    });
  };

  const selectCustomer = (id) => {
    setFilterCustId(id);
    setView('Transactions');
  };

  const clearFilters = () => {
    setFilterCustId('');
    setFilterDate('');
    setFilterStatus('All');
  };

  return (
    <div className="credit-sale-page">
      {/* Standard Header Pattern */}
      <div className="page-header">
        <h2 className="page-title">{tr('creditSale.title')}</h2>
        <div className="header-actions" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="outstanding-badge" style={{ fontSize: 13, background: '#fff', border: '1px solid #e2e8f0', padding: '5px 12px', borderRadius: 6, color: '#64748b' }}>
            {tr('creditSale.netOutstanding')}: <strong style={{ color: '#dc2626' }}>₹{fmtNum(totalOutstanding)}</strong>
          </div>
          {can('credit_sales', 'create') && (

            <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(!showForm); setEditRow(null); resetForm(); }}>
              {showForm ? `✕ ${tr('common.close')}` : `＋ ${tr('creditSale.newCreditSale')}`}
            </button>
          )}
        </div>
      </div>

      {msg && <div style={{ padding: '8px 12px', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{msg}</div>}

      {/* --- MULTI-ROW CREATE FORM --- */}
      {showForm && !editRow && (
        <div className="section-card">
          <div className="section-header">{tr('creditSale.recordNew')}</div>
          <div style={{ padding: '0 16px 16px' }}>
            <div className="form-row" style={{ marginBottom: 16 }}>
              <div className="form-group" style={{ maxWidth: 200 }}><label>{tr('common.date')}</label>
                <input type="date" className="form-control" value={form.sale_date}
                  onChange={e => setForm({ ...form, sale_date: e.target.value })} />
              </div>
            </div>

            <table className="data-table" style={{ marginBottom: 16 }}>
              <thead>
                <tr>
                  <th style={{ width: '24%' }}>{tr('common.customer')}</th>
                  <th style={{ width: '16%' }}>{tr('common.product')}</th>
                  <th className="right-align" style={{ width: '11%' }}>{tr('common.qty')}</th>
                  <th className="right-align" style={{ width: '11%' }}>{tr('common.rate')} (₹)</th>
                  <th className="right-align" style={{ width: '13%' }}>{tr('common.amount')} (₹)</th>
                  <th>{tr('common.remarks')}</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => (
                  <tr key={idx}>
                    <td>
                      <select className="form-control" value={it.customer_id} onChange={e => updateItem(idx, 'customer_id', e.target.value)}>
                        <option value="">{tr('creditSale.selectOption')}</option>
                        {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
                      </select>
                    </td>
                    <td>
                      <select className="form-control" value={it.product_type} onChange={e => updateItem(idx, 'product_type', e.target.value)}>
                        <option value="">{tr('creditSale.selectOption')}</option>
                        {(products || []).map(p => <option key={p.id} value={p.code}>{p.name}</option>)}
                      </select>
                    </td>
                    <td><input type="number" className="form-control right-align" value={it.qty} placeholder="0.000" onChange={e => updateItem(idx, 'qty', e.target.value)} /></td>
                    <td><input type="number" className="form-control right-align" value={it.rate} placeholder="0.00" onChange={e => updateItem(idx, 'rate', e.target.value)} /></td>
                    <td><input type="number" className="form-control right-align" value={it.amount} placeholder="0.00" onChange={e => updateItem(idx, 'amount', e.target.value)} /></td>
                    <td><input className="form-control" value={it.remarks} placeholder={tr('creditSale.remarksPlaceholder')} onChange={e => updateItem(idx, 'remarks', e.target.value)} /></td>
                    <td>
                      {form.items.length > 1 && (
                        <button className="btn btn-sm" style={{ color: 'var(--danger)' }} onClick={() => removeItem(idx)}>🗑️</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button className="btn btn-secondary btn-sm" onClick={addItem}>＋ {tr('creditSale.addAnotherRow')}</button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ fontSize: 14 }}>{tr('creditSale.grandTotal')}: <strong style={{ color: 'var(--primary)' }}>₹{fmtNum(grandTotal)}</strong></div>
                <button className="btn btn-primary" onClick={handleSaveNew} disabled={saving}>{saving ? tr('common.saving') : `💾 ${tr('creditSale.saveCreditSales')}`}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- SINGLE-ROW EDIT FORM --- */}
      {editRow && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ {tr('creditSale.editCreditSale')} — {editRow.customer_name}</div>
          <div className="form-row" style={{ padding: '16px 16px 0' }}>
            <div className="form-group" style={{ flex: 1 }}><label>{tr('common.date')}</label>
              <input type="date" className="form-control" value={editRow.sale_date} disabled />
            </div>
            <div className="form-group" style={{ flex: 2 }}><label>{tr('common.customer')}</label>
              <input className="form-control" value={editRow.customer_name || ''} disabled />
            </div>
            <div className="form-group" style={{ flex: 1 }}><label>{tr('common.product')}</label>
              <select className="form-control" value={editRow.product_type} onChange={e => setEditRow({ ...editRow, product_type: e.target.value })}>
                <option value="">{tr('creditSale.selectOption')}</option>
                {(products || []).map(p => <option key={p.id} value={p.code}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}><label>{tr('common.qty')}</label>
              <input type="number" className="form-control" value={editRow.qty}
                onChange={e => {
                  const q = e.target.value;
                  const amt = q && editRow.rate ? (parseFloat(q) * parseFloat(editRow.rate)).toFixed(2) : editRow.amount;
                  setEditRow({ ...editRow, qty: q, amount: amt });
                }} placeholder="0.000" />
            </div>
            <div className="form-group" style={{ flex: 1 }}><label>{tr('common.rate')} (₹)</label>
              <input type="number" className="form-control" value={editRow.rate}
                onChange={e => {
                  const r = e.target.value;
                  const amt = editRow.qty && r ? (parseFloat(editRow.qty) * parseFloat(r)).toFixed(2) : editRow.amount;
                  setEditRow({ ...editRow, rate: r, amount: amt });
                }} placeholder="0.00" />
            </div>
            <div className="form-group" style={{ flex: 1 }}><label>{tr('common.amount')} (₹)</label>
              <input type="number" className="form-control" value={editRow.amount} onChange={e => setEditRow({ ...editRow, amount: e.target.value })} placeholder="0.00" />
            </div>
          </div>
          <div className="form-row" style={{ padding: '0 16px 16px' }}>
            <div className="form-group" style={{ flex: 1 }}><label>{tr('creditSale.remarksNotes')}</label>
              <input type="text" className="form-control" value={editRow.remarks} onChange={e => setEditRow({ ...editRow, remarks: e.target.value })} placeholder={tr('creditSale.remarksPlaceholderFull')} />
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px', display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleSaveEdit} disabled={saving}>{saving ? tr('common.saving') : `💾 ${tr('common.update')}`}</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>{`✕ ${tr('common.cancel')}`}</button>
          </div>
        </div>
      )}

      {/* --- View Switcher & Filters (Integrated) --- */}
      <div className="section-card" style={{ marginBottom: 16 }}>
        <div className="form-row" style={{ padding: 16, alignItems: 'center', gap: 16 }}>
          <div className="view-toggle-mini" style={{ display: 'flex', background: '#f1f5f9', padding: 3, borderRadius: 8 }}>
            <button className={`btn-toggle ${view === 'Parties' ? 'active' : ''}`} onClick={() => setView('Parties')}>{tr('creditSale.parties')}</button>
            <button className={`btn-toggle ${view === 'Transactions' ? 'active' : ''}`} onClick={() => setView('Transactions')}>{tr('creditSale.transactions')}</button>
          </div>

          <div style={{ borderLeft: '1px solid #e2e8f0', height: 24 }} />

          {view === 'Parties' ? (
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <input className="form-control" placeholder={tr('creditSale.searchParty')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ height: 38 }} />
            </div>
          ) : (
            <>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <select className="form-control" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ height: 38 }}>
                  {STATUSES.map(s => <option key={s} value={s}>{s} {tr('creditSale.bills')}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <input type="date" className="form-control" value={filterDate} onChange={e => setFilterDate(e.target.value)} style={{ height: 38 }} />
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <select className="form-control" value={filterCustId} onChange={e => setFilterCustId(e.target.value)} style={{ height: 38 }}>
                  <option value="">{tr('creditSale.allCustomers')}</option>
                  {customers.map(c => (
                    <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={clearFilters}>{tr('common.reset')}</button>
            </>
          )}
        </div>
      </div>

      {view === 'Parties' ? (
        /* --- PARTIES VIEW (CARDS) --- */
        <div className="party-grid">
          {customers.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase())).map(c => (
            <div key={c.customer_id} className="party-card-mini" onClick={() => selectCustomer(c.customer_id)}>
              <div className="p-header">
                <div className="p-name">{c.name}</div>
                <div className="p-badge">{c.open_bills} {tr('creditSale.bills')}</div>
              </div>
              <div className="p-body">
                <div className="p-stat">
                  <label>{tr('creditSale.outstanding')}</label>
                  <div className="p-val danger">₹{fmtNum(c.balance)}</div>
                </div>
                <div className="p-stat">
                  <label>{tr('creditSale.creditLimit')}</label>
                  <div className="p-val">₹{fmtNum(c.credit_limit)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* --- TRANSACTIONS VIEW (TABLE) --- */
        <div className="section-card">
          <div className="section-header">{tr('creditSale.transactionRecords')}</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', width: '40px' }}>#</th>
                  <th style={{ textAlign: 'left' }}>{tr('common.date')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('creditSale.billNo')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('common.customer')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('common.product')}</th>
                  <th className="right-align">{tr('common.qty')}</th>
                  <th className="right-align">{tr('common.rate')}</th>
                  <th className="right-align">{tr('common.amount')}</th>
                  <th className="right-align">{tr('common.paid')}</th>
                  <th className="right-align">{tr('common.balance')}</th>
                  <th style={{ textAlign: 'center' }}>{tr('common.status')}</th>
                  <th style={{ textAlign: 'center' }}>{tr('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan="10"><Spinner /></td></tr> : transactions.length === 0 ? (
                  <tr><td colSpan="10" style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>{tr('creditSale.noTransactions')}</td></tr>
                ) : transactions.map((t, idx) => (
                  <tr key={t.id}>
                    <td>{idx + 1}</td>
                    <td>{fmtDate(t.sale_date)}</td>
                    <td style={{ fontWeight: 700, color: 'var(--primary)', fontFamily: 'monospace' }}>{t.bill_no}</td>
                    <td>{t.party_name}</td>
                    <td><span className="badge-outline">{t.product_code}</span></td>
                    <td className="right-align">{t.qty || '0.000'}</td>
                    <td className="right-align">₹{fmtNum(t.rate)}</td>
                    <td className="right-align">₹{fmtNum(t.amount)}</td>
                    <td className="right-align" style={{ color: '#059669' }}>₹{fmtNum(t.paid)}</td>
                    <td className="right-align" style={{ color: '#dc2626', fontWeight: 700 }}>₹{fmtNum(t.balance)}</td>
                    <td style={{ textAlign: 'center' }}>
                      {(() => {
                        const amt = Number(t.amount);
                        const paid = Number(t.paid);
                        const bal = Number(t.balance);

                        let displayStatus = t.status;
                        if (bal <= 0) displayStatus = 'Paid';
                        else if (paid > 0) displayStatus = 'Partial';
                        else displayStatus = 'Pending';

                        return <span className={`status-badge ${displayStatus.toLowerCase()}`}>{tr(`creditSale.status.${displayStatus.toLowerCase()}`)}</span>;
                      })()}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <button className="btn-icon-sm collect" title={tr('creditSale.collectPayment')} onClick={() => { setShowPayment(t.id); setPayAmt(''); }} disabled={t.status === 'Paid'}>💳</button>
                        {(isAdmin || t.status !== 'Paid') && (
                          <button className="btn-icon-sm edit" title={tr('common.edit')} onClick={() => openEdit(t)}>✏️</button>
                        )}
                        {(isAdmin || t.status !== 'Paid') && (
                          <button className="btn-icon-sm delete" title={tr('common.delete')} onClick={() => setConfirmDelete(t)}>🗑️</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayment && (() => {
        const row = transactions.find(t => t.id === showPayment);
        if (!row) return null;
        const remaining = Number(row.balance) - (Number(payAmt) || 0);

        return (
          <div className="cs-modal-overlay" onClick={() => setShowPayment(null)}>
            <div className="cs-modal-card" onClick={e => e.stopPropagation()}>
              <div className="cs-modal-header">
                <h3>{tr('creditSale.collectPayment')}</h3>
                <button className="cs-close-btn" onClick={() => setShowPayment(null)}>✕</button>
              </div>

              <div className="cs-modal-body">
                <div className="cs-info-grid">
                  <div className="cs-info-tile">
                    <label>{tr('common.customer')}</label>
                    <div className="cs-info-text">{row.party_name}</div>
                  </div>
                  <div className="cs-info-tile">
                    <label>{tr('creditSale.billNumber')}</label>
                    <div className="cs-info-text mono">{row.bill_no}</div>
                  </div>
                </div>

                <div className="cs-balance-highlight">
                  <label>{tr('creditSale.amountDue')}</label>
                  <div className="cs-balance-amt">₹{fmtNum(row.balance)}</div>
                </div>

                <div className="cs-amount-input-wrap">
                  <label>{tr('creditSale.amountToCollect')} (₹)</label>
                  <input
                    type="number"
                    className="cs-input-xl"
                    placeholder="0.00"
                    value={payAmt}
                    onChange={e => setPayAmt(e.target.value)}
                    autoFocus
                  />
                  {payAmt > 0 && (
                    <div className={`cs-remaining-tag ${remaining <= 0 ? 'fully-paid' : ''}`}>
                      {remaining <= 0 ? `✓ ${tr('creditSale.fullPayment')}` : tr('creditSale.balanceLeft', { amt: fmtNum(remaining) })}
                    </div>
                  )}
                </div>
              </div>

              <div className="cs-modal-footer">
                <button className="btn btn-primary btn-full" onClick={() => collect(showPayment)} disabled={saving || !payAmt}>
                  {saving ? tr('creditSale.processing') : tr('creditSale.confirmPayment')}
                </button>
                <button className="btn btn-secondary btn-full" onClick={() => setShowPayment(null)}>{tr('common.cancel')}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!confirmDelete}
        title={tr('creditSale.deleteTransaction')}
        message={tr('creditSale.deleteConfirm', { bill: confirmDelete?.bill_no })}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
